/**
 * InvoicedRoutePick · iter303ak-fix37 — Invoiced · T50 · Action 10
 * ────────────────────────────────────────────────────────────────────
 * Auto-suggests next status (paid / overdue) from T30 payment outcome.
 * Operator confirms or overrides. Terminal task for Invoiced.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, CheckCircle2, AlertTriangle, DollarSign } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InvoicedRoutePick({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parent = useMemo(() => (
    capturedData?.appraisal_record || capturedData?.appraisal || {}
  ), [capturedData]);

  const invoicedPayments = useMemo(() => (parent.invoiced_payments || {}), [parent]);
  const summary = useMemo(() => {
    let due = 0, paid = 0, allPaid = true, anyPaid = false;
    for (const [, b] of Object.entries(invoicedPayments)) {
      const d = Number(b?.due_amount || 0);
      const p = Number(b?.paid_total || 0);
      due += d; paid += p;
      if (p > 0) anyPaid = true;
      if (d - p > 0.01) allPaid = false;
    }
    const remaining = Number((due - paid).toFixed(2));
    const suggested = (allPaid && anyPaid) ? 'paid' : 'overdue';
    return { due: Number(due.toFixed(2)), paid: Number(paid.toFixed(2)), remaining, suggested };
  }, [invoicedPayments]);

  const [pick, setPick] = useState(value?.pick || summary.suggested);
  useEffect(() => { if (!value?.pick && summary.suggested) setPick(summary.suggested); }, [summary.suggested]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (onChange) onChange({ pick, suggested: summary.suggested }); }, [pick, summary.suggested]);  // eslint-disable-line react-hooks/exhaustive-deps

  const overridePick = pick && pick !== summary.suggested;

  return (
    <div className="space-y-4" data-testid="wf-invoiced-route">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <ArrowRight className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Route to next status</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Auto-suggests from T30 payment outcome. Override if the client asked for a payment plan or you know a payment is en-route.'}
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 rounded-lg bg-slate-800 text-white flex items-center gap-6 flex-wrap"
        data-testid="wf-invoiced-route-totals">
        <span className="text-[10px] uppercase tracking-widest opacity-70">Payment summary</span>
        <span><DollarSign size={12} className="inline mr-0.5 opacity-70" />Due <b className="font-mono">${fmt(summary.due)}</b></span>
        <span><DollarSign size={12} className="inline mr-0.5 opacity-70 text-emerald-300" />Paid <b className="font-mono text-emerald-300">${fmt(summary.paid)}</b></span>
        <span>Remaining <b className={`font-mono ${summary.remaining > 0.01 ? 'text-rose-300' : 'text-emerald-300'}`}>${fmt(summary.remaining)}</b></span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[{ key: 'paid', label: 'Paid', color: 'emerald', desc: 'Move to Paid status — Certificate of Completion + Thank Client + Maintenance Upsell + Warranty send.' },
          { key: 'overdue', label: 'Overdue', color: 'rose', desc: 'Move to Overdue status — Contact Client + Warning Letters + potential payment plan negotiation.' }].map(opt => (
          <button key={opt.key} onClick={() => setPick(opt.key)}
            data-testid={`wf-invoiced-route-${opt.key}`}
            className={`text-left p-4 border-2 rounded-xl transition-all
              ${pick === opt.key
                ? `border-${opt.color}-500 bg-${opt.color}-50 shadow`
                : 'border-slate-200 bg-white hover:border-slate-300'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-sm font-semibold ${pick === opt.key ? `text-${opt.color}-900` : 'text-slate-800'}`}>
                Move to <b>{opt.label}</b>
              </span>
              {opt.key === summary.suggested && (
                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-300 rounded-full px-2 py-0.5">
                  Auto-suggested
                </span>
              )}
              {pick === opt.key && overridePick && (
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5">
                  Override
                </span>
              )}
            </div>
            <div className="text-xs text-slate-600">{opt.desc}</div>
          </button>
        ))}
      </div>

      {overridePick && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded flex items-start gap-2"
          data-testid="wf-invoiced-route-override-banner">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5" />
          <div className="text-xs text-amber-900">
            <b>Override — you're picking "{pick}" instead of "{summary.suggested}".</b> Make sure the payment data at T30 reflects reality before committing.
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!pick}
            data-testid="wf-invoiced-route-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Confirm & Route
          </button>
        </div>
      </div>
    </div>
  );
}
